import {
  Injectable,
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  EmbeddedViewRef,
  ComponentRef,
  Inject,
} from '@angular/core';
import { SnackbarConfig } from '../../interfaces/snackbar';
import { SnackbarComponent } from '../../components/snackbar/snackbar.component';
import { SNACKBAR_CONTAINER_PROVIDER } from '../../providers/snackbar-container.provider';

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  private readonly DEFAULT_DURATION = 5000;
  private snackbarRef: ComponentRef<SnackbarComponent> | null = null;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
    @Inject(SNACKBAR_CONTAINER_PROVIDER) private snackbarContainer: string
  ) {}

  show(config: SnackbarConfig): void {
    this.destroyExistingSnackbar();
    this.snackbarRef = this.createSnackbarComponent(config.message);
    this.setupAnimationListener(this.snackbarRef);
    this.attachToDOM(this.snackbarRef);
    this.setupAutoHide(this.snackbarRef, config.duration);
  }

  private setupAnimationListener(componentRef: ComponentRef<SnackbarComponent>): void {
    const subscription = componentRef.instance.animationDone.subscribe(() => {
      this.destroyExistingSnackbar();
    });

    componentRef.onDestroy(() => {
      subscription.unsubscribe();
    });
  }

  private setupAutoHide(componentRef: ComponentRef<SnackbarComponent>, duration?: number): void {
    const hideTimeout = setTimeout(() => {
      if (this.snackbarRef) {
        this.snackbarRef.instance.isVisible.set(false);
      }
    }, duration || this.DEFAULT_DURATION);
    componentRef.onDestroy(() => {
      clearTimeout(hideTimeout);
    });
  }

  private createSnackbarComponent(message: string) {
    const componentRef = createComponent(SnackbarComponent, {
      environmentInjector: this.injector,
    });

    componentRef.setInput('message', message);
    return componentRef;
  }

  private destroyExistingSnackbar(): void {
    if (this.snackbarRef) {
      this.appRef.detachView(this.snackbarRef.hostView);
      this.snackbarRef.destroy();
      this.snackbarRef = null;
    }
  }

  private attachToDOM(componentRef: ComponentRef<SnackbarComponent>): void {
    this.appRef.attachView(componentRef.hostView);
    const domElement = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0];

    const container = document.querySelector(this.snackbarContainer);
    if (container) {
      container.appendChild(domElement);
    } else {
      document.body.appendChild(domElement);
    }
  }
}
